import { action, computed, observable } from 'mobx';
import React from 'react';
import { APP_CONFIG } from 'appConfig';

import { Partner } from './planningFor/model';

import { CalculationMode } from 'common/interfaces';

export enum Component {
  PLANNING_FOR = 'PLANNING_FOR',
  CURRENT_INCOME = 'CURRENT_INCOME',
  INSURANCE = 'INSURANCE',
  RETIREMENT = 'RETIREMENT',
  RENT = 'RENT',
}

interface RentFlowStep {
  title: string;
  component: Component;
}

export const rentFlow: { [key in CalculationMode]: RentFlowStep[] } = {
  [CalculationMode.REQUIRED]: [
    { title: 'finQuestionnary_forWhoPlan', component: Component.PLANNING_FOR },
    { title: 'finQuestionnary_aboutFamily_tabName', component: Component.CURRENT_INCOME },
    { title: 'finQuestionnary_insurance', component: Component.INSURANCE },
    { title: 'finQuestionnary_countInRetirement', component: Component.RETIREMENT },
    { title: 'finQuestionnary_rent_tabName', component: Component.RENT },
  ],
  [CalculationMode.MANUAL]: [{ title: 'finQuestionnary_rent_tabName', component: Component.RENT }],
  [CalculationMode.CALCULATED]: [{ title: 'finQuestionnary_rent_tabName', component: Component.RENT }],
  [CalculationMode.NONE]: [],
};

export class RentModalsStore {
  closeRent = () => {};

  constructor(closeRent: () => void, flow?: CalculationMode) {
    this.flow = flow || CalculationMode.NONE;
    this.closeRent = closeRent;
  }
  @observable loading = false;

  @action setLoading = (isLoading: boolean) => this.loading = isLoading;

  @observable flow = CalculationMode.NONE;
  @observable flowTab = 0;

  @action setFlow = (flow: CalculationMode) => (this.flow = flow);
  @action setRentTab = (tab: number) => (this.flowTab = tab);
  @action resetFlow = (flow: CalculationMode) => {
    const prevFlow = this.flow;
    this.setFlow(flow);
    this.setRentTab(rentFlow[flow].findIndex((tab) => tab.title === rentFlow[prevFlow][this.flowTab].title) || 0);
  };

  @action handleRentTabChange = (_e: React.ChangeEvent<{}>, tab?: number) => {
    if (tab !== undefined) {
      if (APP_CONFIG.uiSwitchTabs || tab < this.flowTab) {
        this.flowTab = tab;
      }
    }
  };

  @observable rentSubmitting = false; // show some sort of loading

  @action setRentSubmitting = (isSubmitting: boolean) => (this.rentSubmitting = isSubmitting);

  handleCloseRent = () => this.closeRent();

  @observable partner: null | Partner = null;

  @action
  onNextRentTab = () => {
    if (rentFlow[this.flow][this.flowTab + 1]) {
      this.flowTab += 1;
    }
  };

  @action
  onBackRentTab = () => {
    this.flowTab === 0 ? this.setFlow(CalculationMode.NONE) : (this.flowTab -= 1);
  };

  @computed
  get rentFormKey(): string {
    return `key_${this.flowTab}`;
  }

  @computed
  get rentNextText(): string {
    if (this.flow === CalculationMode.NONE) return '';
    if (this.flowTab === rentFlow[this.flow].length - 1) {
      return 'general_saveAndClose';
    }

    return rentFlow[this.flow][this.flowTab + 1]?.title || 'general_close';
  }

  @computed
  get rentBackText(): string {
    if (this.flow === CalculationMode.NONE) return '';
    return rentFlow[this.flow][this.flowTab - 1]?.title || 'finQuestionnary_rentTypeSelection';
  }

  @computed
  get component(): string {
    return rentFlow[this.flow][this.flowTab]?.component || '';
  }

  @computed
  get hideRentNextButton(): boolean {
    return !rentFlow[this.flow][this.flowTab + 1];
  }
}
